import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy — ChefConnect Italia" },
      {
        name: "description",
        content:
          "Informativa sulla privacy di ChefConnect Italia: quali dati raccogliamo, come li usiamo, i tuoi diritti ai sensi del GDPR.",
      },
      { property: "og:title", content: "Privacy Policy — ChefConnect Italia" },
      {
        property: "og:description",
        content:
          "Quali dati raccogliamo, come li usiamo e i tuoi diritti ai sensi del GDPR.",
      },
      { property: "og:url", content: "https://chefconnectitalia.com/privacy" },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: "https://chefconnectitalia.com/privacy" }],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <article className="container mx-auto max-w-3xl px-4 py-16 sm:py-24">
      <p className="text-xs font-medium uppercase tracking-[0.2em] text-gold">Legale</p>
      <h1 className="mt-3 font-display text-5xl">Privacy Policy</h1>
      <p className="mt-3 text-sm text-muted-foreground">Ultimo aggiornamento: 27 giugno 2026</p>

      <div className="mt-10 space-y-10 text-[15px] leading-relaxed text-muted-foreground">
        <p>
          La presente informativa descrive le modalità di trattamento dei dati
          personali raccolti tramite il sito{" "}
          <a href="https://chefconnectitalia.com" className="text-foreground hover:text-gold">
            chefconnectitalia.com
          </a>{" "}
          ("ChefConnect" o "il Sito"). Il documento è redatto ai sensi del
          Regolamento UE 2016/679 ("GDPR") e della normativa italiana applicabile.
        </p>

        <Section title="1. Titolare del trattamento">
          <p>
            Titolare del trattamento è ChefConnect Italia. Per qualsiasi
            richiesta puoi scrivere a{" "}
            <a href="mailto:privacy@chefconnectitalia.com" className="text-foreground hover:text-gold">
              privacy@chefconnectitalia.com
            </a>
            .
          </p>
        </Section>

        <Section title="2. Dati raccolti">
          <ul className="list-disc space-y-2 pl-5">
            <li>
              <strong className="text-foreground">Dati di registrazione</strong>:
              email, password (cifrata), nome visualizzato, ruolo (cliente o chef).
            </li>
            <li>
              <strong className="text-foreground">Dati di profilo chef</strong>:
              bio, foto, città, lingue, prezzi, specialità, documenti di
              verifica (carta d'identità, P.IVA, attestati HACCP).
            </li>
            <li>
              <strong className="text-foreground">Dati di contatto</strong>:
              messaggi e richieste di preventivo scambiati con gli chef.
            </li>
            <li>
              <strong className="text-foreground">Dati tecnici</strong>:
              indirizzo IP, log di accesso, identificativi del dispositivo,
              metadati delle pagine visitate.
            </li>
            <li>
              <strong className="text-foreground">Dati di pagamento</strong>:
              gestiti direttamente dal fornitore di pagamento (Stripe). Non
              memorizziamo numeri di carta sui nostri server.
            </li>
          </ul>
        </Section>

        <Section title="3. Finalità e base giuridica">
          <ul className="list-disc space-y-2 pl-5">
            <li>
              Erogazione del servizio (registrazione, profilo, ricerca, contatto
              tra clienti e chef) — base giuridica: esecuzione del contratto
              (art. 6.1.b GDPR).
            </li>
            <li>
              Adempimenti fiscali e contabili — base giuridica: obbligo di
              legge (art. 6.1.c GDPR).
            </li>
            <li>
              Sicurezza della piattaforma, prevenzione frodi, log tecnici —
              base giuridica: legittimo interesse (art. 6.1.f GDPR).
            </li>
            <li>
              Comunicazioni di servizio (notifiche, reset password, conferme) —
              esecuzione del contratto.
            </li>
            <li>
              Comunicazioni promozionali — solo previo consenso esplicito,
              revocabile in qualsiasi momento.
            </li>
          </ul>
        </Section>

        <Section title="4. Fornitori e responsabili esterni">
          <p>Per erogare il servizio utilizziamo fornitori selezionati che agiscono come responsabili del trattamento:</p>
          <ul className="mt-3 list-disc space-y-2 pl-5">
            <li>
              <strong className="text-foreground">Lovable Cloud</strong> —
              hosting, database, autenticazione, storage e funzioni serverless.
            </li>
            <li>
              <strong className="text-foreground">Stripe</strong> — gestione dei
              pagamenti per gli abbonamenti Premium.
            </li>
            <li>
              <strong className="text-foreground">Provider email</strong> —
              invio email transazionali (conferme, reset, notifiche).
            </li>
          </ul>
        </Section>

        <Section title="5. Trasferimenti extra-UE">
          <p>
            Alcuni fornitori possono trattare dati al di fuori dell'Unione
            Europea. In tal caso il trasferimento avviene sulla base delle
            Clausole Contrattuali Standard approvate dalla Commissione Europea
            o di altri meccanismi conformi al GDPR.
          </p>
        </Section>

        <Section title="6. Tempi di conservazione">
          <ul className="list-disc space-y-2 pl-5">
            <li>Dati account: per tutta la durata della registrazione.</li>
            <li>Dati di profilo pubblico chef: finché il profilo resta pubblicato.</li>
            <li>Messaggi e richieste: 24 mesi dalla creazione.</li>
            <li>Log tecnici: massimo 12 mesi.</li>
            <li>Documenti fiscali: 10 anni come previsto per legge.</li>
          </ul>
        </Section>

        <Section title="7. I tuoi diritti">
          <p>In qualità di interessato puoi esercitare i seguenti diritti:</p>
          <ul className="mt-3 list-disc space-y-2 pl-5">
            <li>Accesso ai tuoi dati personali.</li>
            <li>Rettifica di dati inesatti.</li>
            <li>Cancellazione ("diritto all'oblio").</li>
            <li>Limitazione del trattamento.</li>
            <li>Portabilità dei dati.</li>
            <li>Opposizione al trattamento basato sul legittimo interesse.</li>
            <li>Revoca del consenso, ove applicabile.</li>
            <li>Reclamo al Garante per la protezione dei dati personali.</li>
          </ul>
          <p className="mt-4">
            Per esercitare i tuoi diritti scrivi a{" "}
            <a href="mailto:privacy@chefconnectitalia.com" className="text-foreground hover:text-gold">
              privacy@chefconnectitalia.com
            </a>
            .
          </p>
        </Section>

        <Section title="8. Cookie">
          <p>
            Il Sito utilizza cookie tecnici e, previo consenso, cookie di
            analisi. Per il dettaglio consulta la{" "}
            <Link to="/cookie" className="text-foreground hover:text-gold">
              Cookie Policy
            </Link>
            .
          </p>
        </Section>

        <Section title="9. Modifiche">
          <p>
            Possiamo aggiornare la presente informativa. La data riportata in
            alto indica l'ultima revisione. Gli aggiornamenti rilevanti saranno
            comunicati via email o all'interno della piattaforma.
          </p>
        </Section>
      </div>
    </article>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="font-display text-2xl text-foreground">{title}</h2>
      <div className="mt-3">{children}</div>
    </section>
  );
}